/**
 * usePatients Hook - AWS API Version
 *
 * Manages patient data fetching and updates via the AWS API layer.
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '../lib/api';

export const usePatients = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getPatients();
      setPatients(data.patients || data || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      setPatients([]);
    }
    setLoading(false);
  }, []);

  const updatePatient = useCallback(async (id, updates) => {
    if (!id || !updates || Object.keys(updates).length === 0) {
      console.warn('Invalid patient ID or empty updates');
      return { success: false, error: new Error('Invalid update payload') };
    }

    try {
      await api.updatePatient(id, updates);

      // If status changed to 'contacted', create an outreach log
      if (updates.status === 'contacted') {
        try {
          // Find the patient to get clinic_id
          const patient = patients.find(p => p.id === id);
          await api.createOutreachLog({
            patient_id: id,
            channel: 'manual',
            clinic_id: patient?.clinic_id || null,
          });
        } catch (logError) {
          console.error('Failed to create outreach log:', logError);
        }
      }

      // Optimistically update local state
      setPatients(prev =>
        prev.map(p => (p.id === id ? { ...p, ...updates } : p))
      );

      return { success: true };
    } catch (error) {
      console.error(`Failed to update patient ${id}:`, error);
      return { success: false, error };
    }
  }, [patients]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  return { patients, loading, refetch: fetchPatients, updatePatient, setPatients };
};
